// The assistant surfaces a single canonical link for an in-app destination — the mobile
// universal-link `https://apps.nownia.com/time/...` (or the `orion://time/...` deep-link). On the
// native app that opens the matching screen; on desktop web `apps.nownia.com` serves the
// app-download landing page, so a "Connect your calendar" link dead-ends there instead of reaching
// Settings. This resolver adapts such a link to the equivalent orion-web route so the surface — not
// the assistant — decides where it lands (the same split `commonTypes/Navigation` draws for nav).
//
// Returns a MOUNT-RELATIVE route (prefix with `useBasePath()`), or `null` when the href isn't a
// known in-app destination — those keep their normal external behavior. Only routes that actually
// exist under `pages/` are mapped; an unrecognized `time/...` path stays external rather than
// inventing a web screen that doesn't exist.

// The canonical universal-link host is `apps.nownia.com`, but the assistant occasionally emits the
// singular `app.nownia.com` — an unclaimed domain that dead-ends on a Wix "domain isn't connected"
// page on desktop web. Treat it as an alias so a malformed host still gets adapted to the in-app
// route instead of leaking through as a dead external link.
import { contactPath, contactThreadPath, type ContactId } from './contact-book';
import { parseProfileRoute } from './profile-link-view';
import { profileThreadPath } from './profile-thread-target';
import { chaseItemPath } from './chase-item';

const NOWNIA_HOSTS = new Set(['apps.nownia.com', 'app.nownia.com']);

// The Symphony-brand app-links domain. Unlike nownia (a general host whose app links live under a
// `time/` prefix), this domain exists ONLY to route into the app — every path is an app destination
// (it 302s to the app-invite/deferred-deeplink page for a visitor without the app), so no prefix is
// required. A leading `time/` is still tolerated, matching the native resolvers.
const APP_LINK_HOSTS = new Set(['symphony.wix.app']);

// `symphony.wix.com` is deliberately NOT treated as an in-app host, even though it is where web serves.
// Claiming our own origin here looks obvious and is wrong three ways: the host is SHARED — `/agent/mcp`,
// `/agent/agent-card.json` and `/individuals-chat/*` are harness endpoints, and the assistant is instructed
// to hand out the first — so claiming them router-pushes a working URL into a 404; a pathname is not
// guaranteed to be single-slash-rooted, so `//evil.com` would render as a protocol-relative OFF-origin href
// with the external branch's `rel="noreferrer"` deliberately omitted; and running the app→web adapter over
// an address that is already a web route truncates real ones (`workspace/<slug>/agents`, `dashboard/<slug>`).
// A same-origin link stays external: the browser navigates, the redirects in next.config.js catch the app
// spellings, and the cost is a page load rather than a soft push.

const STATIC_ROUTES: Record<string, string> = {
  '': '/',
  home: '/',
  chat: '/',
  tasks: '/tasks',
  pulses: '/pulses',
  routines: '/pulses',
  knowledge: '/knowledge',
  // Every app the user's agents have built, and — above them — the shares they have been OFFERED and
  // not yet answered. The Apps screen shipped with no deeplink at all, which is what made the link a
  // share now mails to somebody with no account resolve to nothing on all three surfaces.
  apps: '/apps',
  // The singular, id-less form. `app/<id>` is handled below; `app` on its own reaches here, and a
  // truncated or un-interpolated link has to land on the screen rather than nowhere.
  app: '/apps',
  packs: '/packs',
  // The credits / plan-picker deeplink. `packages` is the canonical token (web's real `/packages`
  // page — the Symphony plan picker); `upgrade`/`credits`/`plans` are aliases the native resolvers
  // accept too, so the same universal link resolves identically on all three surfaces (#7667/#7668).
  // Distinct from `packs` above (the Symphony Packs feature) — different word, no collision. On native
  // this opens the CreditsUpgrade destination URL; on web it's a real in-app route, so it maps here.
  packages: '/packages',
  upgrade: '/packages',
  credits: '/packages',
  plans: '/packages',
  // The native resolvers accept these aliases too — keep the three surfaces identical.
  agents: '/agents',
  team: '/agents',
  agentslist: '/agents',
  // Connectors moved under Settings and grew two halves — Tools and Channels. Every alias below
  // resolves to a TAB, never to the bare page, so a link that used to name one subject still lands
  // on that subject rather than on whichever tab happens to be the default
  // (`docs/connectors-page-design.md` §1.2).
  connectors: '/settings/connectors/tools',
  // Going to be retired as producer-less — until a jot/invite link wrapping `apps.nownia.com/calendar`
  // turned up, which the sweep had not looked at. It means what `settings/calendars` means, so it
  // lands on the same row.
  calendar: '/settings/connectors/tools/google-calendar',
  notifications: '/notifications',
  // The account-level Compliance page (design §2.6): `symphony.wix.app/compliance` on the phones, the
  // same word here — the Settings row and the Maestro's link both land on it.
  compliance: '/compliance',
  // The Wins page — `symphony.wix.app/wins` opens the same screen on both phones.
  wins: '/wins',
  settings: '/settings',
  // The Share-the-app screen (both phones map `share` to it): web opens the Settings modal from
  // `?share=1` (`useShareOnArrival` in ShareAppRow / AccessSection).
  share: '/settings?share=1',
  // Calendar connection is NOT a Settings section on web — it lives on the Connectors page's Tools
  // tab (AppsSection/ConnectorsBody, CalendarConnect). A `/settings#calendars` target dead-ends: the
  // Settings page has no calendars section and no hash handling at all, so the connect flow never
  // opens (#7156). The assistant emits this path from seven prompt/tool sites, so it must keep
  // resolving — and it points AT the calendar row, which is what the link promises.
  'settings/calendars': '/settings/connectors/tools/google-calendar',
  // Settings has no `#data-control` anchor/section either — Delete Account is a plain row on the
  // page (no auto-opened confirm, unlike native's one-shot settingsDataControlRequested flag), so the
  // hash was equally inert. Land on the bare page, where the row is actually visible.
  'settings/data-control': '/settings',
  'settings/models': '/settings/models',
  // The credit statement (Settings → Credit usage). Bare `credits` stays the plan picker, as on both phones.
  'settings/credits': '/settings/credits',
  // Settings → Notification preferences (smart notifications step 4): the same screen both phones push.
  'settings/notifications': '/settings/notifications',
  // No producer was found for this anywhere — but its content is now literally the Channels tab, so
  // pointing it there costs a line and beats retiring it. Deleting a route is only worth it when
  // there is nowhere sensible to send it (§8.1).
  'settings/connections': '/settings/connectors/channels',
  // Kept resolving even though this merge deletes the hub screen, and NOT only for old bookmarks:
  // the deeper `settings/whatsapp/group/<id>` form is the tap target of two live server pushes, and
  // a malformed group link (a truncated push, an id that failed to interpolate) has to fall back to
  // the WhatsApp section rather than nowhere. The parser case is load-bearing either way (§1.2).
  'settings/whatsapp': '/settings/connectors/channels/whatsapp',
  // The owner's Symphony profile links (docs/symphony-profile-links-design.md §8.2) — the same screen on
  // both phones. `profile-links/<id>` (one link's editor) is handled below because it carries an id.
  'profile-links': '/profile-links',
  // The contact book (outreach visibility, design §4). `contact` is the singular alias the native resolvers
  // accept too; `contacts/<id>[/<outreachId>]` is handled below because it carries ids.
  contacts: '/contacts',
  contact: '/contacts',
};

/** A profile link lives on `https://symphony.wix.app` and nowhere else (D2). */
function isCanonicalProfileHost(href: string | undefined | null): boolean {
  try {
    const url = new URL(href ?? '');
    return url.protocol === 'https:' && url.host.toLowerCase() === 'symphony.wix.app';
  } catch {
    return false;
  }
}

export function resolveInAppPath(href: string | undefined | null): string | null {
  const segments = nowniaTimeSegments(href);
  if (!segments) return null;
  const key = segments.map((s) => s.toLowerCase()).join('/');
  if (key in STATIC_ROUTES) return STATIC_ROUTES[key];

  // `settings/whatsapp/group/<id>` — one group's own screen, and the tap target of the two live
  // "group ready" / "group failed" server pushes. Handled here rather than in the static table
  // because it carries an id. A `group` segment with NO id is a truncated push or an id that failed
  // to interpolate; it lands on the WhatsApp section rather than nowhere, which is the same fallback
  // the native resolvers make.
  if (segments[0]?.toLowerCase() === 'settings' && segments[1]?.toLowerCase() === 'whatsapp') {
    const groupId = segments[2]?.toLowerCase() === 'group' ? segments[3] : undefined;
    return groupId
      ? `/settings/connectors/channels/whatsapp/group/${groupId}`
      : STATIC_ROUTES['settings/whatsapp'];
  }

  // The Calendar SCREEN. Bare `calendar` is taken (it means "connect a calendar", above, and a jot/invite
  // link depends on it), so the screen is `calendar/view`. Only the screen's own params pass through —
  // `view`, `day`, `space` (a workspace id) — in the order the link gives them; the page validates them, as
  // both phones do (an unknown view or a malformed day falls back to the default).
  if (segments[0]?.toLowerCase() === 'calendar' && segments[1]?.toLowerCase() === 'view' && segments.length === 2) {
    return `/calendar${calendarQuery(href)}`;
  }

  // One workspace by slug, and its Files & Apps gallery (`workspace/<slug>/files`) — the "Agent
  // Creations" link using-symphony.md tells the assistant to send when a user asks where a generated
  // document lives. Both are real web pages (`pages/workspace/[slug]/index.tsx` and `.../files.tsx`),
  // so leaving them unmapped sent an in-app destination through the EXTERNAL branch: a new tab to
  // symphony.wix.app, which 302s to the app-invite page. Handled here rather than in the static table
  // because the slug is an id. Mirrors the native `workspace/<slug>[/files]` resolvers.
  if (segments[0]?.toLowerCase() === 'workspace' && segments[1]) {
    const slug = segments[1];
    // `workspace/<slug>/goal/<goalId>` — one goal, opened on its workspace's Goals tab. Scoped by
    // workspace rather than a bare `goal/<id>` because every client loads goals per workspace
    // (`useWorkspaceGoals(workspaceId)` here, `fetchGoals(workspaceId)` on native); no surface can
    // resolve a goal from an id alone, so the slug is what makes the link openable at all.
    if (segments[2]?.toLowerCase() === 'goal' && segments[3]) {
      return `/workspace/${slug}/goals?goal=${encodeURIComponent(segments[3])}`;
    }
    // `workspace/<slug>/calendar` — that Space's Calendar: the page redirects to `/calendar?space=<id>`.
    if (segments[2]?.toLowerCase() === 'calendar') return `/workspace/${slug}/calendar`;
    // `workspace/<slug>/analytics` — that Space's site Analytics page (spec 2026-10-25 §5), on all three surfaces.
    if (segments[2]?.toLowerCase() === 'analytics') return `/workspace/${slug}/analytics`;
    // `workspace/<slug>/plan` and `workspace/<slug>/domain` — the site's in-app Plans and Domain pages (plan-domain spec).
    if (segments[2]?.toLowerCase() === 'plan') return `/workspace/${slug}/plan`;
    if (segments[2]?.toLowerCase() === 'domain') return `/workspace/${slug}/domain`;
    return segments[2]?.toLowerCase() === 'files' ? `/workspace/${slug}/files` : `/workspace/${slug}`;
  }

  // `me/<handle>[/<context>]` — someone's PUBLIC profile link, tapped inside the app. On a phone it opens the
  // profile thread straight away (§6.1); web does the same through `/profile-thread`, which POSTs the open and
  // forwards into the chat (or into the editor when it is the reader's own link). The grammar is the one all
  // three surfaces share (`parseProfileRoute`): a malformed context or extra segments open the MAIN profile;
  // a malformed handle is not a profile and stays external. Only the canonical host counts — `me/` was never
  // a nownia path, so `apps.nownia.com/time/me/...` and `orion://` are not profile links.
  if (segments[0]?.toLowerCase() === 'me' && segments[1]) {
    if (!isCanonicalProfileHost(href)) return null;
    const route = parseProfileRoute(segments[1], segments.slice(2));
    return route ? profileThreadPath(route) : null;
  }

  const head = segments[0]?.toLowerCase();
  // `contacts/<id>[/<outreachId>][?kind=recipient]` — one person, or one outreach thread under them
  // (`DEEPLINK_PATHS.contact` / `.contactThread`, the brief's per-item links and the outreach FYI push). The id
  // is a person id unless the link says `kind=recipient`. Segments arrive percent-encoded and `contactPath`
  // encodes, so they are decoded first (a malformed escape keeps the raw segment, as `pulse/` does).
  if ((head === 'contacts' || head === 'contact') && segments[1]) {
    const contact = decodedSegment(segments[1]);
    // `kind` is compared case-insensitively, as both phones do (`?kind=Recipient` is still a recipient).
    const id: ContactId = queryParam(href, 'kind')?.toLowerCase() === 'recipient' ? { recipientId: contact } : { personId: contact };
    return segments[2] ? contactThreadPath(id, decodedSegment(segments[2])) : contactPath(id);
  }

  // `id` comes from a parsed URL pathname, so it is already percent-encoded and path-safe.
  const id = segments[1];
  if (id) {
    // `chase/<itemId>` — one chaser item's page (docs/2026-10-23-chaser-design.md §10), as on both phones.
    // `chaseItemPath` encodes, so the already-encoded segment is decoded first (a malformed escape keeps it raw).
    if (head === 'chase') return chaseItemPath(decodedSegment(id));
    // `needs-input/<taskId>` is the same task screen as `task/<taskId>`.
    if (head === 'task' || head === 'needs-input') return `/tasks/${id}`;
    // A page slug and a whole dashboard both render through the `/pages/<slug>` route on web.
    if (head === 'page' || head === 'dashboard') return `/pages/${id}`;
    // A specific conversation. Web has no `/chat/<id>` path — the home route opens a thread from the
    // `?conversation=` param (see chat-handoff) — so adapt to that. This is the shape the new-message
    // push deeplink carries (`…/chat/<convId>`), so leaving it unmapped would dead-end a web click.
    if (head === 'chat' || head === 'conversation' || head === 'conversations') {
      return `/?conversation=${id}`;
    }
    // One of the user's own packs. PLURAL only — the singular `pack/<inviter>/<slug>/<token>` is a
    // pack INVITE (accept someone else's pack), which is not an in-web destination.
    if (head === 'packs') return `/packs/${id}`;
    // One app, SINGULAR — the destination of the link a share mails to somebody who has no Symphony
    // account yet. The id is accepted and deliberately not routed on: pending offers already lead the
    // Apps screen, so landing there IS landing on the offer, and inventing a per-app highlight would
    // be a fourth thing for the two phones to match for no gain. It is parsed rather than ignored so
    // the link keeps working if that ever changes, and so a bare `app/` cannot dead-end.
    if (head === 'app') return '/apps';
    // One profile link's screen. Ids are GUIDs, so the segment passes through as the path segment it is.
    // `profile-links/<id>/people` is that link's People — who wrote through it, the "See who wrote" of the
    // profile-link digest — which web opens on the same screen with `?tab=people` (both phones: the People screen).
    if (head === 'profile-links') return segments[2]?.toLowerCase() === 'people' ? `/profile-links/${id}?tab=people` : `/profile-links/${id}`;
    // One routine. Web has no routine-detail SCREEN — the list opens one as an expanded card — but that
    // card is addressable (`?openId=`, see use-expanded.ts), so this is an adapted destination like
    // `chat/<id>` above rather than an invented route. It used to stay external on the grounds that "web
    // has none", which sent every routine link the assistant shared to a 404.
    // The id crosses from a path segment into a QUERY value here, and the two encodings differ, so it is
    // normalised rather than passed through or blindly re-encoded. Passing it through splits the param when
    // a raw `&` arrives; re-encoding it doubles an escape that a parsed pathname already carries, turning the
    // producer's `a%26b` into `a%2526b`. Decoding first collapses both to one form. A malformed escape keeps
    // the raw segment rather than throwing — an unopenable link beats a crash in the renderer.
    if (head === 'pulse') {
      let decoded = id;
      try {
        decoded = decodeURIComponent(id);
      } catch {
        // Not valid percent-encoding; encode what arrived.
      }
      return `/pulses?openId=${encodeURIComponent(decoded)}`;
    }
  }
  return null;
}

function decodedSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

const CALENDAR_PARAMS = new Set(['view', 'day', 'space']);

/** The Calendar screen's own params of an href (`?view=&day=&space=`), as a query string; '' when it has none. */
function calendarQuery(href: string | undefined | null): string {
  let params: URLSearchParams;
  try {
    params = new URL(href ?? '').searchParams;
  } catch {
    return '';
  }
  const kept = new URLSearchParams();
  params.forEach((value, name) => {
    if (CALENDAR_PARAMS.has(name) && value) kept.append(name, value);
  });
  const query = kept.toString();
  return query ? `?${query}` : '';
}

/** One query parameter of an href already known to parse (it passed `nowniaTimeSegments`). */
function queryParam(href: string | undefined | null, name: string): string | null {
  try {
    return new URL(href ?? '').searchParams.get(name);
  } catch {
    return null;
  }
}

// The `time/...` path segments of a nownia universal-link (`https://apps.nownia.com/time/...`, or the
// tolerated singular `app.nownia.com`) or an `orion://time/...` deep-link, with the `time` prefix
// stripped. `null` when href is neither.
function nowniaTimeSegments(href: string | undefined | null): string[] | null {
  if (!href) return null;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (url.protocol === 'orion:') {
    // `orion://time/settings/calendars` parses to host `time`, pathname `/settings/calendars`.
    if (url.host.toLowerCase() !== 'time') return null;
    return url.pathname.split('/').filter(Boolean);
  }
  if (url.protocol === 'https:' || url.protocol === 'http:') {
    const host = url.hostname.toLowerCase();
    const segments = url.pathname.split('/').filter(Boolean);
    if (NOWNIA_HOSTS.has(host)) {
      // nownia serves non-app pages too, so only the `time/...` namespace is an app destination.
      if (segments[0]?.toLowerCase() !== 'time') return null;
      return segments.slice(1);
    }
    if (APP_LINK_HOSTS.has(host)) {
      return segments[0]?.toLowerCase() === 'time' ? segments.slice(1) : segments;
    }
  }
  return null;
}

/**
 * The message a conversation deep link points at — `…/<chat|conversation|conversations>/<conversationId>/message/<messageId>`,
 * the same shape (and the same `message` marker) iOS `DeepLink.swift` and Android read for open-at-message. Null when
 * the link names no message. Query/fragment are stripped first, as in `conversationIdFromDeeplink` (lib/notifications-feed.ts).
 * Here rather than beside it because that module is server-side and the notification card needs this in the client.
 */
export function messageIdFromDeeplink(deeplink: string | null | undefined): string | null {
  if (!deeplink) return null;
  const segments = deeplink.split(/[?#]/)[0].split('/').filter(Boolean);
  const markerIndex = segments.findIndex((segment) => ['chat', 'conversation', 'conversations'].includes(segment.toLowerCase()));
  if (markerIndex < 0 || segments[markerIndex + 2]?.toLowerCase() !== 'message') return null;
  const id = segments[markerIndex + 3];
  if (!id) return null;
  // A malformed escape must read as "no target", never throw while a notification card renders.
  try {
    return decodeURIComponent(id);
  } catch {
    return null;
  }
}
